import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Brain, Lightbulb, ChevronRight, Volume2, Sparkles } from 'lucide-react';

// Hafıza Teknikleri Bileşeni - Çağrışım ile öğrenme
export default function MemoryTrick({ title, tricks, onAnswer }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [learnedAll, setLearnedAll] = useState(false);

  const currentTrick = tricks[currentIndex];
  const isLastTrick = currentIndex === tricks.length - 1;

  const handleReveal = () => {
    setRevealed(true);
  };

  const handleNext = () => {
    if (isLastTrick) {
      setLearnedAll(true);
    } else {
      setCurrentIndex(prev => prev + 1);
      setRevealed(false);
    }
  };

  const handleComplete = () => {
    if (onAnswer) onAnswer(true, 1);
  };

  const speakWord = (text) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      utterance.rate = 0.8;
      window.speechSynthesis.speak(utterance);
    }
  };

  if (learnedAll) {
    return (
      <div className="w-full max-w-2xl mx-auto text-center">
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-6">
          <div className="w-20 h-20 mx-auto bg-pink-100 rounded-full flex items-center justify-center mb-4">
            <Sparkles className="w-10 h-10 text-pink-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">Harika! 🧠</h3>
          <p className="text-gray-600 mb-6">
            {tricks.length} hafıza tekniğini öğrendin!
          </p>
          
          <div className="bg-pink-50 rounded-xl p-4 text-left">
            <h4 className="font-bold text-pink-800 mb-3">📝 Öğrendiğin Kelimeler:</h4>
            <div className="space-y-2">
              {tricks.map((trick, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <span className="text-pink-600">✓</span>
                  <span className="font-bold">{trick.word}</span>
                  <span className="text-gray-500">= {trick.translation}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <Button
          onClick={handleComplete}
          className="w-full py-4 text-lg font-bold bg-emerald-500 hover:bg-emerald-600 rounded-xl"
        >
          Devam Et
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-pink-100 rounded-xl">
            <Brain className="w-6 h-6 text-pink-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">{title}</h2>
            <p className="text-sm text-gray-500">Hafıza Teknikleri</p>
          </div>
        </div>
        <div className="text-sm font-medium text-gray-500">
          {currentIndex + 1} / {tricks.length}
        </div>
      </div>

      {/* Progress */}
      <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
        <div 
          className="bg-pink-500 h-2 rounded-full transition-all duration-300"
          style={{ width: `${((currentIndex + 1) / tricks.length) * 100}%` }}
        />
      </div>

      {/* Memory Card */}
      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden mb-6">
        {/* Word Header */}
        <div className="bg-gradient-to-r from-pink-500 to-purple-500 p-6 text-center text-white">
          <div className="flex items-center justify-center gap-3 mb-2">
            <h3 className="text-3xl font-bold">{currentTrick.word}</h3>
            <button 
              onClick={() => speakWord(currentTrick.word)}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
            >
              <Volume2 className="w-5 h-5" />
            </button>
          </div>
          {currentTrick.phonetic && (
            <p className="text-white/80">/{currentTrick.phonetic}/</p>
          )}
        </div>

        <div className="p-6">
          {!revealed ? (
            <div className="text-center">
              <p className="text-gray-600 mb-6">Bu kelimeyi nasıl hatırlayacaksın?</p>
              <button
                onClick={handleReveal}
                className="flex items-center justify-center gap-2 w-full py-4 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-xl font-bold text-lg hover:opacity-90 transition-opacity"
              >
                <Lightbulb className="w-5 h-5" />
                Hafıza Tekniğini Gör
              </button>
            </div>
          ) : (
            <div className="animate-fadeIn space-y-4">
              {/* Translation */}
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 text-center">
                <p className="text-sm text-green-600 mb-1">Türkçe Anlamı</p>
                <p className="text-2xl font-bold text-green-700">{currentTrick.translation}</p>
              </div>

              {/* Memory Trick */}
              <div className="bg-gradient-to-r from-pink-50 to-purple-50 border-2 border-pink-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-pink-100 rounded-lg">
                    <Brain className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <p className="font-bold text-pink-800 mb-2">💡 Hafıza Tekniği</p>
                    <p className="text-gray-700">{currentTrick.trick}</p>
                  </div>
                </div>
              </div>

              {/* Visual Association */}
              {currentTrick.visual && (
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-sm text-blue-600 mb-2">🎨 Görsel Çağrışım</p>
                  <p className="text-gray-700">{currentTrick.visual}</p>
                </div>
              )}

              {/* Example Sentence */}
              {currentTrick.example && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-500 mb-1">📝 Örnek Cümle</p>
                  <p className="text-gray-700 italic">"{currentTrick.example}"</p>
                  {currentTrick.exampleTr && (
                    <p className="text-gray-500 text-sm mt-1">{currentTrick.exampleTr}</p>
                  )}
                </div>
              )}

              {/* Similar Words */}
              {currentTrick.similar && currentTrick.similar.length > 0 && (
                <div className="bg-yellow-50 rounded-xl p-4">
                  <p className="text-sm text-yellow-600 mb-2">🔗 Benzer Kelimeler</p>
                  <div className="flex flex-wrap gap-2">
                    {currentTrick.similar.map((word, idx) => (
                      <span key={idx} className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                        {word}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Next Button */}
      {revealed && (
        <Button
          onClick={handleNext}
          className="w-full py-4 text-lg font-bold bg-pink-500 hover:bg-pink-600 rounded-xl"
        >
          {isLastTrick ? 'Tamamla' : 'Sonraki Teknik'}
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
      )}
    </div>
  );
}
